"use client"

import { useState, useEffect, useContext } from "react"
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Fade,
  useMediaQuery,
  Drawer,
  Badge,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import CustomPagination from "../transaccionComponent/CustomPagination"
import EditIcon from "@mui/icons-material/Edit"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import SearchIcon from "@mui/icons-material/Search"
import BusinessIcon from "@mui/icons-material/Business"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import RefreshIcon from "@mui/icons-material/Refresh"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CloseIcon from "@mui/icons-material/Close"
import TuneIcon from "@mui/icons-material/Tune"
import { ErpContext } from "../../context/ErpContext.js"
import ModalCrearActualizarEntidad from "./ModalCrearActualizarEntidad"

const TablaEntidadProveedor = ({
  entidades,
  searchText,
  setSearchText,
  idTipoEntidadFilter,
  setIdTipoEntidadFilter,
  tipoEntidades: tipoEntidadesProp,
  loading,
  error,
  setError,
  success,
  setSuccess,
  page,
  totalPages,
  totalCount,
  handleChangePage,
  fetchEntidades,
  filtrosAtributos: filtrosAtributosProp,
  setFiltrosAtributos: setFiltrosAtributosProp,
}) => {
  const theme = useTheme()
  const { 
    isNavbarCollapsed, 
    getTipoEntidad, 
    tipoEntidades: tipoEntidadesContext,
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext)
  
  // Usar tipoEntidades de props si están disponibles, sino del contexto
  const tipoEntidades = tipoEntidadesProp || tipoEntidadesContext

  // Media queries para responsive
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))

  // Estados locales del modal
  const [openModal, setOpenModal] = useState(false)
  const [selectedEntidad, setSelectedEntidad] = useState(null)

  // Estados para filtros
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [mostrarFiltroAtributos, setMostrarFiltroAtributos] = useState(false)
  // Usar filtros desde props si están disponibles, sino usar estado local
  const [filtrosAtributosLocal, setFiltrosAtributosLocal] = useState({})
  const filtrosAtributos = filtrosAtributosProp !== undefined ? filtrosAtributosProp : filtrosAtributosLocal
  const setFiltrosAtributos = setFiltrosAtributosProp !== undefined ? setFiltrosAtributosProp : setFiltrosAtributosLocal
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({})

  // Obtener Tipos de Entidad y Atributos
  useEffect(() => {
    if (tipoEntidades.length === 0) {
      getTipoEntidad()
    }
    // Cargar atributos de entidad si no están cargados
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      getEntidadAtributoNoEliminados()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cargar opciones para cada atributo
  useEffect(() => {
    const cargarOpcionesAtributos = async () => {
      if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
        setOpcionesPorAtributo({})
        return
      }

      const opcionesMap = {}
      for (const attr of entidadAtributoNoEliminados) {
        try {
          const opciones = await getEntidadAtributoClasificacionByIdAtributo(attr.id)
          opcionesMap[attr.id] = opciones || []
        } catch (error) {
          console.error(`Error al cargar opciones para atributo ${attr.id}:`, error)
          opcionesMap[attr.id] = []
        }
      }
      setOpcionesPorAtributo(opcionesMap)
    }
    
    cargarOpcionesAtributos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadAtributoNoEliminados])

  // Mapear atributos a campos de la entidad
  const mapearAtributosACampos = () => {
    return entidadAtributoNoEliminados
      ?.filter((attr) => !attr.eliminado)
      .map((attr, index) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        campo: `atributoEntidad${index + 1}`,
        index: index + 1,
      })) || []
  }

  // Manejar cambio en filtros de atributos
  const handleAtributoFilterChange = (campo, valor) => {
    setFiltrosAtributos((prev) => ({
      ...prev,
      [campo]: valor === "" ? null : valor,
    }))
  }

  // Función helper para mapear filtros al formato esperado
  const mapearFiltros = () => {
    const filtrosMapeados = {}
    mapearAtributosACampos().forEach((attr) => {
      const valor = filtrosAtributos[attr.campo]
      if (valor && valor !== "" && valor !== "null") {
        filtrosMapeados[attr.campo] = valor
      }
    })
    return filtrosMapeados
  }

  // Función wrapper para fetchEntidades que siempre incluye los filtros actuales
  const fetchEntidadesConFiltros = () => {
    const filtrosMapeados = mapearFiltros()
    fetchEntidades(filtrosMapeados)
  }

  // Aplicar filtros y recargar datos
  const handleAplicarFiltrosAtributos = () => {
    fetchEntidadesConFiltros()
  }

  // Limpiar filtros de atributos
  const handleLimpiarFiltrosAtributos = () => {
    setFiltrosAtributos({})
    fetchEntidades({})
  }

  // Contar filtros activos
  const cantidadFiltrosAtributosActivos = Object.values(filtrosAtributos).filter(
    (valor) => valor && valor !== "" && valor !== "null"
  ).length

  const toggleFilterDrawer = () => setFilterDrawerOpen(!filterDrawerOpen)

  // Función helper para obtener el nombre del tipo de entidad
  const getTipoEntidadNombre = (idTipoEntidad) => {
    const tipo = tipoEntidades.find(t => t.id === idTipoEntidad)
    return tipo ? tipo.descripcion : "Desconocido"
  }

  // Función helper para obtener el color del tipo de entidad
  const getTipoEntidadColor = (idTipoEntidad) => {
    const tipo = tipoEntidades.find(t => t.id === idTipoEntidad)
    if (!tipo) return "default"
    
    // Mapeo dinámico basado en la descripción
    const descripcion = tipo.descripcion.toLowerCase()
    if (descripcion.includes("cliente")) return "primary"
    if (descripcion.includes("proveedor")) return "secondary" 
    if (descripcion.includes("empleado")) return "success"
    if (descripcion.includes("banco")) return "warning"
    if (descripcion.includes("local") || descripcion.includes("negocio")) return "info"
    if (descripcion.includes("pasajero")) return "error"
    return "default"
  }

  // Abrir modal para EDITAR entidad
  const handleOpenEditModal = (entidad) => {
    setSelectedEntidad(entidad) // Cargar datos en el modal
    setOpenModal(true)
  }

  // Abrir modal para CREAR entidad
  const handleOpenCreateModal = () => {
    setSelectedEntidad({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 1, // Tipo entidad por defecto
    })
    setOpenModal(true)
  }

  // Cerrar modal y actualizar lista si se guardaron cambios
  const handleCloseModal = async (refresh = false) => {
    setOpenModal(false)
    if (refresh) {
      await fetchEntidadesConFiltros()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    }
  }

  const tableStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  }

  return (
    <Box 
      sx={{ 
        height: "100%", 
        display: "flex", 
        flexDirection: "column", 
        overflow: "hidden",
        p: { xs: 0.5, sm: 1 },
        minHeight: 0,
        maxHeight: "100%",
        width: "100%",
        maxWidth: "100%",
        transition: "all 0.3s ease",
      }}
    >
      {/* Encabezado */}
      <Card
        elevation={1}
        sx={{
          mb: 1,
          borderRadius: 1,
          overflow: "visible",
          flexShrink: 0,
        }}
      >
        <CardContent sx={{ p: { xs: 1, sm: 1.5 }, "&:last-child": { pb: { xs: 1, sm: 1.5 } } }}>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            {/* Filtro por Tipo de Entidad */}
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 200 },
                maxWidth: { sm: "20%", md: "15%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            >
              <InputLabel>Tipo de Entidad</InputLabel>
              <Select
                value={idTipoEntidadFilter || ""}
                onChange={(e) => setIdTipoEntidadFilter(e.target.value)}
                label="Tipo de Entidad"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {tipoEntidades && tipoEntidades.map((tipoEntidad) => (
                  <MenuItem key={tipoEntidad.id} value={tipoEntidad.id}>
                    {tipoEntidad.descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Buscador */}
            <TextField
              placeholder="Buscar por nombre, apellido, CUIT o email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                maxWidth: { sm: "60%", md: "40%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Botones de acción */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={filterDrawerOpen ? "Cerrar Más Filtros" : "Mostrar Más Filtros"}>
                <Badge 
                  badgeContent={cantidadFiltrosAtributosActivos} 
                  color="secondary"
                >
                  <IconButton
                    onClick={toggleFilterDrawer}
                    color="primary"
                    size="small"
                    sx={{
                      bgcolor: filterDrawerOpen ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                      "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                    }}
                  >
                    <TuneIcon />
                  </IconButton>
                </Badge>
              </Tooltip>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchEntidadesConFiltros}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 100,
                }}
              >
                Actualizar
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreateModal}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: 2,
                  minWidth: 140,
                  backgroundColor: "#5b5b5f",
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                }}
              >
                Nueva Entidad
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerta de éxito */}
      {success && (
        <Fade in={success}>
          <Alert
            severity="success"
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: 1,
              flexShrink: 0,
            }}
            onClose={() => setSuccess(false)}
          >
            Operación completada con éxito
          </Alert>
        </Fade>
      )}

      {/* Alerta de error */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
            flexShrink: 0,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Drawer de Filtros */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={toggleFilterDrawer}
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            width: 80,
            top: 0,
            height: "100vh",
            backgroundColor: alpha(theme.palette.background.paper, 0.98),
            borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1000,
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              pb: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <IconButton size="small" onClick={toggleFilterDrawer}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {/* Icono de Filtros por Atributos */}
            <Tooltip title="Filtrar por atributos" placement="left">
              <IconButton
                onClick={() => setMostrarFiltroAtributos(!mostrarFiltroAtributos)}
                sx={{
                  color: mostrarFiltroAtributos || cantidadFiltrosAtributosActivos > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                  "& svg": {
                    color: mostrarFiltroAtributos || cantidadFiltrosAtributosActivos > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                  },
                  bgcolor: mostrarFiltroAtributos ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                  "&:hover": {
                    bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                  },
                }}
              >
                <Badge badgeContent={cantidadFiltrosAtributosActivos} color="secondary">
                  <FilterAltIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Divider sx={{ my: 1 }} />
          </Box>
        </Box>
      </Drawer>

      {/* Box de Filtros de Atributos en el componente principal */}
      {mostrarFiltroAtributos && entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && (
        <Paper
          elevation={1}
          sx={{
            p: 1,
            mb: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
              <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
              Filtrar por Atributos de Entidad
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setMostrarFiltroAtributos(false)} 
              sx={{ color: "#5b5b5f" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Filtros por Atributos de Entidad */}
          <Box sx={{ mb: 1 }}>
            {(() => {
              const tieneAtributos = entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0
              
              if (!tieneAtributos) {
                return (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}>
                    No hay atributos de entidad configurados. Ve a Administración → Atributos de Entidad para configurarlos.
                  </Typography>
                )
              }
              
              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  {mapearAtributosACampos().map((attr) => {
                    const opciones = opcionesPorAtributo[attr.id] || []
                    return (
                      <FormControl key={attr.id} fullWidth size="small">
                        <InputLabel>{attr.descripcion}</InputLabel>
                        <Select
                          value={filtrosAtributos[attr.campo] || ""}
                          label={attr.descripcion}
                          onChange={(e) => handleAtributoFilterChange(attr.campo, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Todos</em>
                          </MenuItem>
                          {opciones.map((opcion) => (
                            <MenuItem key={opcion.id} value={opcion.id}>
                              {opcion.descripcion}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )
                  })}
                  {/* Botones de Acción */}
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAplicarFiltrosAtributos}
                      startIcon={<SearchIcon />}
                      sx={{
                        backgroundColor: "#5b5b5f",
                        textTransform: "none",
                        padding: "8px 16px",
                        color: "white",
                        "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                      }}
                    >
                      Aplicar
                    </Button>
                    {cantidadFiltrosAtributosActivos > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleLimpiarFiltrosAtributos}
                        sx={{ 
                          textTransform: "none",
                          color: "#5b5b5f",
                          borderColor: "#5b5b5f",
                          padding: "8px 16px",
                          "&:hover": {
                            borderColor: "rgb(244, 177, 14)",
                            backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                          },
                        }}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Box>
                </Box>
              )
            })()}
          </Box>
        </Paper>
      )}

      {/* Tabla de Clientes */}
      <Paper
        sx={{
          flex: 1,
          m: 1,
          borderRadius: 2,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          overflow: "hidden",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box 
          sx={{ 
            position: "relative", 
            flex: 1, 
            display: "flex", 
            flexDirection: "column", 
            minHeight: 0,
            maxHeight: "100%",
            height: "100%",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Overlay de carga */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              flex: 1,
              overflowX: "auto !important",
              overflowY: "auto !important",
              minHeight: 0,
              height: "100%",
              maxHeight: "100%",
              width: "100%",
              position: "relative",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#555",
                },
              },
              "&::-webkit-scrollbar-corner": {
                backgroundColor: "#f1f1f1",
              },
            }}
          >
            <Table 
              stickyHeader 
              sx={{ 
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "4px 8px",
                      whiteSpace: "nowrap",
                      height: "32px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Apellido
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    DNI / CUIT / CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "8%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  {/* Columnas dinámicas de atributos */}
                  {entidadAtributoNoEliminados && entidadAtributoNoEliminados
                    .filter((attr) => !attr.eliminado)
                    .map((attr) => {
                      const numAtributos = entidadAtributoNoEliminados.filter(a => !a.eliminado).length
                      const anchoBase = 5 + 12 + 10 + 10 + 8 + 15 + 12 + 8 // Suma de anchos fijos
                      const anchoRestante = Math.max(0, 100 - anchoBase) // Ancho restante
                      const anchoPorAtributo = numAtributos > 0 ? Math.max(anchoRestante / numAtributos, 8) : 10
                      
                      return (
                        <TableCell
                          key={attr.id}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            backgroundColor: "#5b5b5f",
                            color: "white",
                            width: `${anchoPorAtributo}%`,
                            padding: "4px 8px",
                            height: "32px",
                          }}
                        >
                          {attr.descripcion}
                        </TableCell>
                      )
                    })}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "8%",
                      padding: "4px 8px",
                      height: "32px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entidades.length > 0 ? (
                  entidades.map((entidad) => (
                    <TableRow
                      key={entidad.id}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          transition: "background-color 0.2s ease",
                        },
                        transition: "background-color 0.2s",
                        height: "36px",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Chip
                          label={entidad.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            minWidth: 35,
                            height: 20,
                            justifyContent: "center",
                            borderColor: "#5b5b5f",
                            color: "#5b5b5f",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PersonIcon 
                            fontSize="small" 
                            sx={{ 
                              color: "#5b5b5f", 
                              fontSize: "0.875rem",
                              flexShrink: 0,
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: "0.813rem",
                              color: "#212121",
                            }}
                          >
                            {entidad.descripcion || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: "0.813rem",
                            color: "#424242",
                          }}
                        >
                          {entidad.apellido || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <BadgeIcon 
                            fontSize="small" 
                            sx={{ 
                              color: "#5b5b5f", 
                              fontSize: "0.875rem",
                              flexShrink: 0,
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: "0.813rem",
                              color: "#424242",
                              fontFamily: "monospace",
                            }}
                          >
                            {entidad.dniCuitCuil || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Chip
                          label={getTipoEntidadNombre(entidad.idTipoEntidad)}
                          size="small"
                          color={getTipoEntidadColor(entidad.idTipoEntidad)}
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                          }}
                        >
                          {entidad.email && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <EmailIcon 
                                fontSize="small" 
                                sx={{ 
                                  color: "#5b5b5f", 
                                  fontSize: "0.75rem",
                                  flexShrink: 0,
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.75rem",
                                  color: "#424242",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "180px",
                                }}
                                title={entidad.email}
                              >
                                {entidad.email}
                              </Typography>
                            </Box>
                          )}
                          {entidad.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <PhoneIcon 
                                fontSize="small" 
                                sx={{ 
                                  color: "#5b5b5f", 
                                  fontSize: "0.75rem",
                                  flexShrink: 0,
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.75rem",
                                  color: "#424242",
                                }}
                              >
                                {entidad.telefono}
                              </Typography>
                            </Box>
                          )}
                          {!entidad.email && !entidad.telefono && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: "0.75rem",
                                color: "#9e9e9e",
                                fontStyle: "italic",
                              }}
                            >
                              Sin contacto
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Tooltip
                          title={
                            entidad.direccion || entidad.localidad || entidad.provincia
                              ? `${entidad.direccion || ""} ${entidad.localidad || ""} ${entidad.provincia || ""}`.trim()
                              : "Sin ubicación"
                          }
                          arrow
                          placement="top"
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationOnIcon 
                              fontSize="small" 
                              sx={{ 
                                color: "#5b5b5f", 
                                fontSize: "1rem",
                                flexShrink: 0,
                              }} 
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.875rem",
                                color: "#424242",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "160px",
                              }}
                            >
                              {entidad.localidad 
                                ? `${entidad.localidad}${entidad.provincia ? `, ${entidad.provincia}` : ""}` 
                                : "—"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {/* Celdas dinámicas de atributos */}
                      {entidadAtributoNoEliminados && entidadAtributoNoEliminados
                        .filter((attr) => !attr.eliminado)
                        .map((attr, index) => {
                          const opcionAtributo = entidad[`opcionAtributo${index + 1}`]
                          const valorAtributo = opcionAtributo?.descripcion || null
                          return (
                            <TableCell
                              key={attr.id}
                              sx={{
                                padding: "2px 8px",
                                borderBottom: "1px solid #e0e0e0",
                                fontSize: "0.813rem",
                              }}
                            >
                              {valorAtributo ? (
                                <Chip
                                  label={valorAtributo}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    height: 20,
                                    borderColor: "#5b5b5f",
                                    color: "#5b5b5f",
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "#9e9e9e",
                                    fontStyle: "italic",
                                  }}
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          )
                        })}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "2px 8px",
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: "0.813rem",
                        }}
                      >
                        <Tooltip title="Editar entidad" arrow>
                          <IconButton
                            onClick={() => handleOpenEditModal(entidad)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.08)",
                              color: "#5b5b5f",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.15)",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <EditIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={8 + (entidadAtributoNoEliminados?.filter((attr) => !attr.eliminado).length || 0)} 
                      align="center" 
                      sx={{ py: 3 }}
                    >
                      {loading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 2 }}>
                          <CircularProgress size={30} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.813rem" }}>
                            Cargando Entidades...
                          </Typography>
                        </Box>
                      ) : searchText ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 2 }}>
                          <SearchIcon sx={{ fontSize: 36, color: "text.secondary", opacity: 0.5 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.813rem" }}>
                            No se encontraron Entidades con ese criterio de búsqueda
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Intenta con otros términos de búsqueda
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 2 }}>
                          <BusinessIcon sx={{ fontSize: 36, color: "text.secondary", opacity: 0.5 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.813rem" }}>
                            No hay Entidades registradas
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Crea una nueva entidad para comenzar
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
      
      {/* Paginación - Fuera del contenedor con overflow hidden */}
      <Box 
        sx={{ 
          flexShrink: 0, 
          p: { xs: 0.5, sm: 1 },
          backgroundColor: "white",
          position: "relative",
        }}
      >
        <CustomPagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          vieneDe="tablaEntidades"
          onPageChange={handleChangePage}
        />
      </Box>

      {/* Modal de Crear / Editar Entidad */}
      {openModal && (
        <ModalCrearActualizarEntidad
          open={openModal}
          handleClose={handleCloseModal}
          entidad={selectedEntidad}
          idTipoEntidad={selectedEntidad?.idTipoEntidad || 1}
        />
      )}
    </Box>
  )
}

export default TablaEntidadProveedor
